import React, { useEffect, useMemo, useRef, useState } from "react";
import { compareSchoolYearDesc } from "../../../util/schoolYear";
import { useLocation } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import useOrganizationSetting from "../../../hooks/useOrganizationSetting";
import useRegistrationInfo from "../../../hooks/useRegistrationInfo";
import useFlexibleData from "../../../hooks/useFlexibleData";
import { useAuthContext } from "../../../context/AuthContext";
import { useUIContext } from "../../../context/UIContext";
import FiltersBar from "../../../unicomponents/Dropdowns/FilterBar";
import LoadingInside from "../../../unicomponents/LoadingInside";
import SimpleAlertMessageBlock from "../../../unicomponents/SimpleAlertMessageBlock";
import { checkHrteachers } from "../../../util/checkPermissions";
import { generateRegistrationRows } from "../../../util/dealingArrObj";
import styles from "./CustomPrint.module.css";
import ShineschoolPhysicalReportForm from "./customPrints/ShineschoolPhysicalReportForm";

const CUSTOM_PRINT_COMPONENTS = {
  ShineschoolPhysicalReportForm: ShineschoolPhysicalReportForm,
};

function getCustomPrintConfig(flexibleData, schoolId) {
  const customPrint = flexibleData?.[0]?.data_value?.customPrint;

  if (Array.isArray(customPrint)) {
    return (
      customPrint.find(
        (item) => !item.schoolid || item.schoolid?.toString() === schoolId,
      ) || null
    );
  }

  return customPrint || null;
}

export default function CustomPrint() {
  const location = useLocation();
  const paths = location.pathname.split("/");
  const schoolId = paths[1];
  const contentToPrint = useRef(null);
  const { hasPermission } = useAuthContext();
  const { handleToastCenterTop } = useUIContext();

  const [filters, setFilters] = useState({
    schoolYear: null,
    schoolYearId: "",
    hierarchyOneId: "",
    hierarchyTwoId: "",
    hierarchyThreeId: "",
    organPathForSort: "N/A",
    selectedStudent: "",
  });
  const [studentList, setStudentList] = useState([]);
  const [selectedStudentData, setSelectedStudentData] = useState(null);
  const [printState, setPrintState] = useState({
    isLoading: false,
    canPrint: false,
    message: "",
  });

  const {
    settingQuery: { data: settingData },
  } = useOrganizationSetting({ schoolid: schoolId });

  const {
    registrationInfoQuery: {
      isLoading: isLoadingRegistration,
      data: registrationInfoData,
    },
    hrTeachersQuery: { data: hrTeachersData },
  } = useRegistrationInfo({ schoolid: schoolId }, { hrTeachers: true });

  const {
    flexibleDataQuery: { data: flexibleData, isLoading: isLoadingFlexibleData },
  } = useFlexibleData({ schoolid: schoolId, main_category: "schoolSetting" });

  const customPrintConfig = useMemo(
    () => getCustomPrintConfig(flexibleData, schoolId),
    [flexibleData, schoolId],
  );
  const ActivePrintComponent = customPrintConfig?.print
    ? CUSTOM_PRINT_COMPONENTS[customPrintConfig.print]
    : null;
  const printTitle = customPrintConfig?.title || "";

  useEffect(() => {
    if (filters.schoolYear !== null || !settingData?.length) return;

    const initialData = [...settingData].sort(
      (a, b) => compareSchoolYearDesc(a.school_year, b.school_year),
    )[0];

    setFilters((prev) => ({
      ...prev,
      schoolYear: initialData.school_year,
      schoolYearId: initialData.id,
    }));
  }, [filters.schoolYear, settingData]);

  useEffect(() => {
    if (!registrationInfoData || !settingData || !filters.schoolYear) {
      setStudentList([]);
      return;
    }

    const sortedRows = generateRegistrationRows(
      registrationInfoData,
      settingData,
      filters.schoolYear,
      filters.organPathForSort,
    );

    if (hasPermission(null, "can_read")) {
      setStudentList(sortedRows);
      return;
    }

    if (checkHrteachers(hrTeachersData, "", filters.schoolYear)) {
      const filteredHrTeacherRows = hrTeachersData.filter(
        (student) => student.schoolYear === filters.schoolYear,
      );
      const filteredRows = sortedRows.filter((row) =>
        filteredHrTeacherRows.find((data) => data.username === row.username),
      );
      setStudentList(filteredRows);
      return;
    }

    setStudentList([]);
  }, [
    filters.organPathForSort,
    filters.schoolYear,
    hasPermission,
    hrTeachersData,
    registrationInfoData,
    settingData,
  ]);

  useEffect(() => {
    if (
      !hrTeachersData ||
      !filters.schoolYear ||
      hasPermission(null, "can_read") ||
      !checkHrteachers(hrTeachersData, "", filters.schoolYear)
    ) {
      return;
    }

    const thisYearData = hrTeachersData.find(
      (row) => row.schoolYear === filters.schoolYear,
    );
    if (!thisYearData?.organization_path) return;

    const splited = thisYearData.organization_path.split("/");
    setFilters((prev) => {
      if (prev.organPathForSort === thisYearData.organization_path) {
        return prev;
      }

      return {
        ...prev,
        hierarchyOneId: splited[2] || "",
        hierarchyTwoId: splited[3] || "",
        hierarchyThreeId: splited[4] || "",
        organPathForSort: thisYearData.organization_path,
        selectedStudent: "",
      };
    });
  }, [filters.schoolYear, hasPermission, hrTeachersData]);

  useEffect(() => {
    if (!filters.selectedStudent) {
      setSelectedStudentData(null);
      return;
    }

    const selectedObject = studentList.find(
      (row) => row.username === filters.selectedStudent,
    );
    setSelectedStudentData(selectedObject || null);
  }, [filters.selectedStudent, studentList]);

  useEffect(() => {
    setPrintState({
      isLoading: false,
      canPrint: false,
      message: "",
    });
  }, [customPrintConfig?.print, filters.selectedStudent, filters.schoolYearId]);

  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    const selectedOptionText = e.target.selectedOptions[0].text;
    const dataName = e.target.selectedOptions[0].getAttribute("data-name");
    const organPath = dataName ? `${dataName}/${value}` : "N/A";

    if (name === "0") {
      setFilters((prev) => ({
        ...prev,
        schoolYear: selectedOptionText,
        schoolYearId: value,
        hierarchyOneId: "",
        hierarchyTwoId: "",
        hierarchyThreeId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
    }

    if (name === "1") {
      setFilters((prev) => ({
        ...prev,
        hierarchyOneId: value,
        hierarchyTwoId: "",
        hierarchyThreeId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
    }

    if (name === "2") {
      setFilters((prev) => ({
        ...prev,
        hierarchyTwoId: value,
        hierarchyThreeId: "",
        organPathForSort: "N/A",
        selectedStudent: "",
      }));
    }

    if (name === "3") {
      setFilters((prev) => ({
        ...prev,
        hierarchyThreeId: value,
        organPathForSort: organPath,
        selectedStudent: "",
      }));
    }

    if (name === "students") {
      setFilters((prev) => ({
        ...prev,
        selectedStudent: value,
      }));
    }
  };

  const triggerPrint = useReactToPrint({
    content: () => contentToPrint.current,
    documentTitle: `${filters.schoolYear || ""}-${printTitle || "CustomPrint"}-${
      selectedStudentData?.fullname || ""
    }`,
    onBeforePrint: () => {
      const content = contentToPrint.current;
      if (content) {
        content.style.width = "210mm";
        content.style.height = "auto";
      }
    },
    removeAfterPrint: true,
    pageStyle: `
      @page {
        size: A4 portrait;
        margin: 0;
      }

      @media print {
        body {
          background-color: transparent !important;
        }

        .no-print {
          display: none !important;
        }

        .print-page {
          display: block;
          width: 210mm;
          margin: 0 auto;
        }

        table {
          page-break-inside: auto;
        }

        tr {
          page-break-inside: avoid;
          page-break-after: auto;
        }

        thead {
          display: table-header-group;
        }

        tfoot {
          display: table-footer-group;
        }
      }
    `,
  });

  const handlePrint = () => {
    if (!customPrintConfig) {
      handleToastCenterTop("이 학교에 설정된 커스텀 인쇄물이 없습니다.");
      return;
    }

    if (!ActivePrintComponent) {
      handleToastCenterTop(
        `등록되지 않은 커스텀 인쇄 컴포넌트입니다: ${customPrintConfig.print}`,
      );
      return;
    }

    if (!filters.selectedStudent) {
      handleToastCenterTop("학생을 선택하세요.");
      return;
    }

    if (printState.isLoading) {
      handleToastCenterTop("인쇄 데이터를 불러오는 중입니다.");
      return;
    }

    if (!printState.canPrint) {
      handleToastCenterTop(printState.message || "인쇄할 데이터가 없습니다.");
      return;
    }

    triggerPrint();
  };

  if (isLoadingRegistration || isLoadingFlexibleData || !settingData) {
    return <LoadingInside />;
  }

  if (!hasPermission(null, "can_access", checkHrteachers(hrTeachersData))) {
    return (
      <SimpleAlertMessageBlock message="담임 또는 접근 권한이 있는 사람만 접근할 수 있습니다." />
    );
  }

  return (
    <div className="basicWidth">
      <div className="container-fluid">
        <div className={`no-print ${styles.controlBar}`}>
          {/* {printTitle && <div className={styles.printTitle}>{printTitle}</div>} */}
          <FiltersBar
            settingData={settingData}
            handleDropdownChange={handleDropdownChange}
            filters={filters}
            studentList={studentList}
            disabled={false}
            showUsername={true}
          />
          <div style={{ width: "70px" }} className="me-1 text-center">
            <button
              className="btn btn-info"
              type="button"
              onClick={handlePrint}
            >
              인쇄
            </button>
          </div>
        </div>

        {!customPrintConfig && (
          <div className={`no-print ${styles.statusText}`}>
            이 학교에 설정된 커스텀 인쇄물이 없습니다.
          </div>
        )}

        {customPrintConfig && !ActivePrintComponent && (
          <div className={`no-print ${styles.statusText}`}>
            등록되지 않은 커스텀 인쇄 컴포넌트입니다: {customPrintConfig.print}
          </div>
        )}

        {printState.message && (
          <div className={`no-print ${styles.statusText}`}>
            {printState.message}
          </div>
        )}

        <div className="d-flex justify-content-center print-page">
          {ActivePrintComponent ? (
            <div ref={contentToPrint}>
              <ActivePrintComponent
                schoolId={schoolId}
                student={selectedStudentData}
                filters={filters}
                schoolYearLabel={filters.schoolYear}
                config={customPrintConfig}
                onPrintStateChange={setPrintState}
              />
            </div>
          ) : (
            <div className={`no-print ${styles.emptyState}`}>
              {customPrintConfig
                ? "커스텀 인쇄 컴포넌트를 찾을 수 없습니다."
                : "설정된 커스텀 인쇄물이 없습니다."}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
